import {timestampDate} from "@bufbuild/protobuf/wkt";
import {createClient, type Transport} from "@connectrpc/connect";
import axios from "axios";
import {apiTransport} from "api/connectTransport";
import {ACCOUNT_ONE_OFF_PURCHASES_PATH} from "api/endpointPaths.mjs";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import {OneOffPurchaseKind, OneOffPurchaseService} from "../../gen/kitecorp/transferify/pay/v1/one_off_purchases_pb";
import {
	type ConvertedCredit,
	NO_PURCHASES,
	type PurchaseKind,
	type PurchaseListing,
	purchaseListingOf,
	type UnusedPurchase,
	type UsedPurchase,
} from "./unusedPurchases";

/**
 * The buyer's own one-off purchases (api kitecorp/transferify-api-service#2196, ui#3004): `GET
 * /account/one-off-purchases`, the unused ones and, since kitecorp/transferify-api-service#3181, the used ones whose
 * statutory withdrawal period is still open, with the converted credit beside them; and the holder's own conversion of
 * an unused purchase into credit toward a plan (kitecorp/transferify-ui#3839). The withdrawal itself is a support
 * request (`oneOffWithdrawal.oneOffWithdrawalRequest`), not a call of this class.
 *
 * The call is authenticated, which follows from the purchase rather than being a separate policy: a purchase
 * belongs to an account.
 *
 * <b>Every call carries a timeout</b>, for the reason `home/overLimit/OverLimitApi` states: axios waits
 * forever by default and this app sets no global bound, so a stalled read would hold the card on nothing.
 *
 * <b>Both DTOs are bound to the generated schema</b> (`api/schemaAssert`, below), and the listing is STILL
 * read through `purchaseListingOf` rather than a cast, because the two guard against different things.
 * The binding holds this build to the snapshot it compiled against, so an api rename or retype fails
 * `tsc` naming the field. The parser holds it to whatever api answers at runtime — one ahead of the snapshot
 * may list a field this build does not know — and drops an entry that is not the documented shape rather than
 * rendering an undefined amount.
 */

const READ_TIMEOUT_MS = 15_000;
/**
 * How long the browser waits for a conversion's answer. Ended by an abort signal in the browser only, never by a
 * Connect deadline: a deadline is also sent as a header the bridge hands to the api, which could stop the api in the
 * middle of a conversion. A repeat press is safe, because the api answers the same conversion.
 */
const CONVERT_TIMEOUT_MS = 30_000;

/**
 * The wire enum for each kind the listing names: the Connect call spells a kind as the proto enum, the REST routes as
 * text. Only an allowance is ever convertible today, but the map is total so a listed kind can never reach the call
 * without a spelling.
 */
const CONNECT_KINDS: Record<PurchaseKind, OneOffPurchaseKind> = {
	OVER_LIMIT_ALLOWANCE: OneOffPurchaseKind.OVER_LIMIT_ALLOWANCE,
	PERSONALIZATION_CREDIT: OneOffPurchaseKind.PERSONALIZATION_CREDIT,
};

/** What a pressed conversion answers: the credit the api made out of the purchase. */
export interface OneOffPurchaseConversion {
	purchaseId: string;
	kind: PurchaseKind;
	/** The credit made, in `currency`'s minor units: the `expectedMinor` of the press that made it. */
	creditedMinor: number;
	currency: string;
	/** When the api converted it, as an ISO instant; null where the answer left it out. */
	convertedAt: string | null;
}

/*
 * Compile-time bindings to the generated spec (api/schemaAssert.ts). A backend rename, retype or
 * nullability change fails `tsc -b` naming the field, instead of surfacing as entries `purchaseListingOf`
 * silently drops at runtime (kitecorp/transferify-ui#3026).
 */
export type _UnusedOneOffPurchase = AssertNoSpecDrift<
	SpecDrift<UnusedPurchase, Schemas["UnusedOneOffPurchase"]>>;
export type _WithdrawableUsedOneOffPurchase = AssertNoSpecDrift<
	SpecDrift<UsedPurchase, Schemas["WithdrawableUsedOneOffPurchase"]>>;
export type _ConvertedCredit = AssertNoSpecDrift<
	SpecDrift<ConvertedCredit, Schemas["ConvertedCredit"]>>;

export class UnusedPurchasesApi {
	/**
	 * The account's purchases, newest payment first: the unused ones and the used ones still inside their
	 * withdrawal period (empty lists when there are none), with the converted credit beside them: null when the api
	 * states none or could not read the balance.
	 *
	 * <b>A 404 is "nothing", not a failure.</b> This ui may reach production before the api that serves
	 * the route (kitecorp/transferify-ui#3004 ships independently of api#2196), and against such an api the
	 * card and the note must render exactly what an account with no purchases sees: nothing. Every other
	 * failure — a 401 the token refresh could not rescue, a 5xx, a timeout — rejects, so the caller can
	 * tell "the api has no such purchases" from "the api did not answer" and retry the latter.
	 */
	static async list(): Promise<PurchaseListing> {
		try {
			const {data} = await axios.get<unknown>(ACCOUNT_ONE_OFF_PURCHASES_PATH, {timeout: READ_TIMEOUT_MS});
			return purchaseListingOf(data);
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				return NO_PURCHASES;
			}
			throw error;
		}
	}

	/**
	 * Turns an unused allowance into credit toward a plan, on its holder's press
	 * (`OneOffPurchaseService.ConvertOneOffPurchase`, api kitecorp/transferify-api-service#3183, over the Connect
	 * transport of kitecorp/transferify-ui#3771).
	 *
	 * `expectedMinor` is the credit the buyer was shown: the api refuses the press (`amountChanged`) when it would
	 * make another, so the holder never converts for a figure they did not read. `termsVersion` is the version this
	 * bundle shows, which the api records as the one the holder chose under. Safe to repeat: for a purchase already
	 * converted the api answers the same conversion. Refusals arrive as rejections carrying the `ErrorInfo` reason
	 * (`api/apiErrorIds.oneOffPurchaseConversionRefusalOf`), never classified by their localized sentence; a wait past
	 * {@link CONVERT_TIMEOUT_MS} rejects too, as a transport failure.
	 *
	 * `purchaseId` and `kind` are the ones asked for, because the answer repeats neither. The transport is a
	 * parameter so a test can swap in its own `fetch`.
	 */
	static async convert(
		kind: PurchaseKind,
		purchaseId: string,
		termsVersion: string,
		expectedMinor: number,
		transport: Transport = apiTransport,
	): Promise<OneOffPurchaseConversion> {
		const answer = await createClient(OneOffPurchaseService, transport).convertOneOffPurchase({
			purchaseId,
			kind: CONNECT_KINDS[kind],
			termsVersion,
			expectedMinor: BigInt(expectedMinor),
		}, {signal: AbortSignal.timeout(CONVERT_TIMEOUT_MS)});
		return {
			purchaseId,
			kind,
			creditedMinor: Number(answer.creditedMinor),
			currency: answer.currency,
			convertedAt: answer.convertedAt === undefined ? null : timestampDate(answer.convertedAt).toISOString(),
		};
	}
}
